import { Badge } from '@civilgrid/ui/Badge';
import { DetailCard, DetailCardLink } from '@civilgrid/ui/DetailCard';
import { GridCellExpand } from '@civilgrid/ui/GridCellExpand';
import { GridCellLink } from '@civilgrid/ui/GridCellLink';
import { GridCellText } from '@civilgrid/ui/GridCellText';
import { Icon } from '@civilgrid/ui/Icon';
import { Popover, PopoverContent, PopoverTrigger } from '@civilgrid/ui/Popover';
import { SvgIcon } from '@civilgrid/ui/SvgIcon';
import type { TableColumn } from '@civilgrid/ui/Table';
import type { RowData, TableMeta } from '@tanstack/react-table';
import { format, formatDistanceToNow } from 'date-fns';
import type React from 'react';
import { useSnapshot } from 'valtio';
import { formatUTCDate } from '~/pages/homeViewV2/utils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '../../../../../../common/posthogEvents';
import type { ConflictFeatureRow, ProjectConflict, ProjectConflictAgency } from '../../../../../../common/types';
import { formatOverlapArea, formatOverlapPercent, getColorFromRgb, getFormattedConflictCount } from '../../../common';
import { getLinkHost, getPdfFileName, toAbsoluteHttpUrl } from '../../../common/detailCardHelpers';
import store from '../../../store';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    projectId?: string;
    organizationId?: string;
  }
}

export type AgencyRow = ProjectConflictAgency & {
  rowType: 'agency';
  subRows: ConflictRow[];
  overlapCount: number;
  startDate: string | null;
  endDate: string | null;
};
export type ConflictRow = ProjectConflict & {
  rowType: 'conflict';
  overlapsWindow: boolean;
  agencyPdfLink: string | null;
  /** The same PDF signed as an attachment, when the server could sign it. */
  agencyPdfDownloadLink: string | null;
  agencyNotes: string | null;
  agencySiteLink: string | null;
};
export type ConflictTableRow = AgencyRow | ConflictRow;

/** Groups a layer's project conflicts under their agency, matched via `details.civilgrid_id` = `agencyId`. */
export function groupConflictsByAgency(
  agencies: readonly ProjectConflictAgency[],
  conflicts: readonly ProjectConflict[],
  layerId: string,
  window?: { overlapStartMs?: number | null; overlapEndMs?: number | null } | null,
  conflictFeatures?: readonly ConflictFeatureRow[] | null,
): AgencyRow[] {
  const agenciesById = new Map(agencies.map((a) => [a.id, a]));
  const conflictFeaturesById = new Map((conflictFeatures ?? []).map((f) => [f.id, f]));

  const groupedByAgency: AgencyRow[] = [];
  const groupsByAgencyId = new Map<string, AgencyRow>();
  for (const conflict of conflicts) {
    if (conflict.layerId !== layerId) continue;

    const agencyId = conflict.agencyId as string | undefined;
    const agency = agencyId ? agenciesById.get(agencyId) : undefined;
    if (!agency) continue;

    let group = groupsByAgencyId.get(agency.id);
    if (!group) {
      group = {
        ...agency,
        rowType: 'agency',
        subRows: [],
        overlapCount: 0,
        startDate: window?.overlapStartMs != null ? new Date(window.overlapStartMs).toISOString() : null,
        endDate: window?.overlapEndMs != null ? new Date(window.overlapEndMs).toISOString() : null,
      };
      groupsByAgencyId.set(agency.id, group);
      groupedByAgency.push(group);
    }
    const overlapsWindow = conflictFeaturesById.get(conflict.id)?.window != null;
    group.subRows.push({
      ...conflict,
      rowType: 'conflict',
      overlapsWindow,
      agencyPdfLink: (agency.pdfLink as string) ?? null,
      agencyPdfDownloadLink: (agency.pdfDownloadLink as string) ?? null,
      agencyNotes: (agency.publicNotes as string) || null,
      agencySiteLink: (agency.siteLink as string) || null,
    });
    if (overlapsWindow) group.overlapCount += 1;
  }

  return groupedByAgency;
}

/** The conflict's agency PDF link: the attachment link that saves it when there is one, else the PDF's own link. */
export function agencyPdfHref(row: Pick<ConflictRow, 'agencyPdfLink' | 'agencyPdfDownloadLink'>) {
  return row.agencyPdfDownloadLink || row.agencyPdfLink || undefined;
}

/**
 * Ids of the conflict rows whose feature is selected on the map, for the Table's `highlightedRowIds`.
 * Every conflict table reads the one `store.selectedFeature`, so they stay in sync with each other and the map.
 */
export function useSelectedConflictRowIds(rows: readonly ConflictTableRow[]): ReadonlySet<string> {
  const selectedFeatureId = useSnapshot(store).selectedFeature?.id?.toString();
  const ids = new Set<string>();
  if (!selectedFeatureId) return ids;
  for (const agency of rows) {
    if (agency.rowType !== 'agency') continue;
    for (const conflict of agency.subRows) {
      if (conflict.featureId?.toString() === selectedFeatureId) ids.add(conflict.id);
    }
  }
  return ids;
}

/** Selects and zooms the map to a conflict row's feature; shared by every cell so clicking anywhere in the row acts like a row click. */
function selectConflictOnMap(conflict: ProjectConflict) {
  store.selectConflict(conflict);
  capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2FeatureSelected, {
    project_id: store.project?.id,
    organization_id: store.project?.organization,
    accessed_from: 'tabDetails',
    feature_id: conflict.featureId,
  });
}

/** Selects the conflict and captures the detail popover opening, separately from plain feature selection. */
function openConflictPopover(conflict: ProjectConflict, meta: TableMeta<ConflictTableRow> | undefined) {
  capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ConflictPopoverOpened, {
    project_id: meta?.projectId,
    organization_id: meta?.organizationId,
    agency_id: conflict.agencyId,
    feature_id: conflict.featureId,
  });
}

/** Color indicator for a conflict's map feature, with a show-on-map pin while its row is hovered or selected. */
function ConflictColorDot({ conflict }: { conflict: ProjectConflict }) {
  const color = getColorFromRgb(conflict.rgb) || 'red';
  const isSelected = useSnapshot(store).selectedFeature?.id?.toString() === conflict.featureId?.toString();

  return (
    <div className="ml-auto flex shrink-0 items-center gap-3.5">
      <span className="sr-only">{isSelected ? 'Selected on map' : 'Show on map'}</span>
      <SvgIcon
        icon="showOnMap"
        size={14}
        className={`text-blue-50 ${isSelected ? 'visible' : 'invisible group-data-[hovered=true]/row:visible'}`}
      />
      <div className="flex h-5 w-5 items-center justify-center">
        <div className="inline-flex h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

/** Wraps a conflict row's cell content so clicking/hovering anywhere in the row selects and zooms the map to its feature. */
function ConflictMapCell({ conflict, children }: { conflict: ProjectConflict; children?: React.ReactNode }) {
  return (
    <button
      type="button"
      className="flex h-full w-full min-w-0 items-center text-xs cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-50"
      onMouseEnter={() => store.hoverConflict(conflict)}
      onMouseLeave={() => store.resetHoveredFeature()}
      onClick={() => selectConflictOnMap(conflict)}
    >
      {children}
    </button>
  );
}

export const conflictColumns: TableColumn<ConflictTableRow>[] = [
  {
    id: 'jurisdiction',
    header: 'Jurisdiction',
    accessorKey: 'agencyName',
    size: 130,
    cell: ({ row, table }) => {
      const meta = table.options.meta;

      const handleToggle = () => {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2AgencyRowToggled, {
          project_id: meta?.projectId,
          organization_id: meta?.organizationId,
          agency_id: row.original.id,
          expanded: !row.getIsExpanded(),
        });
        row.getToggleExpandedHandler()();
      };

      return row.original.rowType === 'agency' ? (
        <GridCellExpand
          canExpand={row.getCanExpand()}
          isExpanded={row.getIsExpanded()}
          onToggle={handleToggle}
          toggleTestId={`expand-row-toggle-${row.id}`}
        >
          <GridCellLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleToggle();
            }}
          >
            {row.original.agencyName}
          </GridCellLink>
        </GridCellExpand>
      ) : (
        <ConflictMapCell conflict={row.original}>
          <ConflictColorDot conflict={row.original} />
        </ConflictMapCell>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    size: 100,
    cell: ({ row }) => {
      return row.original.rowType === 'agency' ? (
        <Badge color={row.original.overlapCount === 0 ? 'green' : 'red'}>
          {getFormattedConflictCount(row.original.overlapCount)}
        </Badge>
      ) : (
        <ConflictMapCell conflict={row.original}>
          <Badge color={row.original.overlapsWindow ? 'red' : 'green'}>
            {row.original.overlapsWindow ? 'Conflict' : 'No Conflict'}
          </Badge>
        </ConflictMapCell>
      );
    },
  },
  {
    id: 'updatedDate',
    header: 'Updated',
    size: 80,
    minSize: 80,
    cell: ({ row }) => {
      const updated = row.original.rowType === 'agency' ? row.original.lastRefresh : row.original.updatedAt;
      const content = formatUTCDate(updated);
      return row.original.rowType === 'conflict' ? (
        <ConflictMapCell conflict={row.original}>
          <GridCellText>{content}</GridCellText>
        </ConflictMapCell>
      ) : (
        <GridCellText>{content}</GridCellText>
      );
    },
  },
  {
    id: 'overlap',
    header: 'Overlap (Sq Ft)',
    size: 75,
    minSize: 75,
    cell: ({ row }) => {
      if (row.original.rowType !== 'conflict') return <GridCellText>—</GridCellText>;
      return (
        <ConflictMapCell conflict={row.original}>
          <GridCellText>{formatOverlapArea(row.original.projectRightOfWayOverlapArea)}</GridCellText>
        </ConflictMapCell>
      );
    },
  },
  {
    id: 'projectPercentage',
    header: 'Proj %',
    size: 55,
    minSize: 55,
    cell: ({ row }) => {
      if (row.original.rowType !== 'conflict') return <GridCellText>—</GridCellText>;
      return (
        <ConflictMapCell conflict={row.original}>
          <GridCellText>{formatOverlapPercent(row.original.projectRightOfWayOverlapAreaPercentage)}</GridCellText>
        </ConflictMapCell>
      );
    },
  },
  {
    id: 'startDate',
    header: 'Start',
    size: 80,
    minSize: 80,
    cell: ({ row }) => {
      const content = formatUTCDate(row.original.startDate);
      return row.original.rowType === 'conflict' ? (
        <ConflictMapCell conflict={row.original}>
          <GridCellText>{content}</GridCellText>
        </ConflictMapCell>
      ) : (
        <GridCellText>{content}</GridCellText>
      );
    },
  },
  {
    id: 'endDate',
    header: 'End',
    size: 80,
    minSize: 80,
    cell: ({ row }) => {
      const content = formatUTCDate(row.original.endDate);
      return row.original.rowType === 'conflict' ? (
        <ConflictMapCell conflict={row.original}>
          <GridCellText>{content}</GridCellText>
        </ConflictMapCell>
      ) : (
        <GridCellText>{content}</GridCellText>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    size: 30,
    minSize: 30,
    cell: ({ row, table }) => {
      if (row.original.rowType !== 'conflict') return null;
      const conflict = row.original;
      const meta = table.options.meta;

      return (
        <Popover>
          <PopoverTrigger
            data-testid={`conflict-detail-popover-trigger-${row.id}`}
            onClick={() => openConflictPopover(conflict, meta)}
            aria-label={`Conflict details, ${formatUTCDate(conflict.startDate)} to ${formatUTCDate(conflict.endDate)}`}
            className="cursor-pointer rounded focus-ring"
          >
            <Icon icon="exclamationCircled" />
          </PopoverTrigger>
          <PopoverContent align="center" side="left" className="w-[360px]">
            <DetailCard
              variant="plain"
              data-testid={`conflict-detail-popover-${row.id}`}
              rows={[
                { label: 'Agency', value: (conflict.details.agencyname as string) ?? '' },
                { label: 'Overlap (sq ft)', value: formatOverlapArea(conflict.projectRightOfWayOverlapArea) },
                { label: 'Year Start', value: formatUTCDate(conflict.startDate) },
                { label: 'Year End', value: formatUTCDate(conflict.endDate) },
                {
                  label: 'Confirmed',
                  value: (() => {
                    const dateUpdated = conflict.details.date_updated as string | undefined;
                    const parsed = dateUpdated ? new Date(dateUpdated) : null;
                    if (!parsed || Number.isNaN(parsed.getTime())) return '-';
                    return (
                      <>
                        <span className="font-normal">{format(parsed, 'MM/dd/yyyy')}</span>
                        <span className="text-orange-60"> · {formatDistanceToNow(parsed, { addSuffix: true })}</span>
                      </>
                    );
                  })(),
                },
                {
                  label: 'Forecasted',
                  value:
                    typeof conflict.details.forecasted === 'boolean'
                      ? conflict.details.forecasted
                        ? 'Yes'
                        : 'No'
                      : '-',
                },
                {
                  label: 'PDF',
                  value: conflict.agencyPdfLink ? (
                    <DetailCardLink
                      href={agencyPdfHref(conflict)}
                      target="_blank"
                      rel="noreferrer"
                      icon="download"
                      data-testid="conflict-detail-popover-pdf-link"
                      onClick={() =>
                        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ConflictPdfDownloaded, {
                          project_id: meta?.projectId,
                          organization_id: meta?.organizationId,
                          agency_id: conflict.agencyId,
                          feature_id: conflict.featureId,
                        })
                      }
                    >
                      {getPdfFileName(conflict.agencyPdfLink)}
                    </DetailCardLink>
                  ) : (
                    '-'
                  ),
                },
                {
                  label: 'Link',
                  value: (() => {
                    const raw = (conflict.details.agency_link as string) || conflict.agencySiteLink;
                    if (!raw) return '-';

                    const href = toAbsoluteHttpUrl(raw);
                    // an unparseable link is still worth showing, just not clickable
                    if (!href) return raw;

                    return (
                      <DetailCardLink
                        href={href}
                        icon="externalLink"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="conflict-detail-popover-link"
                        onClick={() =>
                          capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ConflictLinkOpened, {
                            project_id: meta?.projectId,
                            organization_id: meta?.organizationId,
                            agency_id: conflict.agencyId,
                            feature_id: conflict.featureId,
                          })
                        }
                      >
                        {getLinkHost(raw)}
                      </DetailCardLink>
                    );
                  })(),
                },
                { label: 'Contact Name', value: (conflict.details.outreach_email_name as string) ?? '-' },
                {
                  label: 'Contact Email',
                  value: conflict.details.outreach_email ? (
                    <DetailCardLink
                      href={`mailto:${conflict.details.outreach_email}`}
                      icon="envelopeClosed"
                      data-testid="conflict-detail-popover-email-link"
                      onClick={() =>
                        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ConflictEmailClicked, {
                          project_id: meta?.projectId,
                          organization_id: meta?.organizationId,
                          agency_id: conflict.agencyId,
                          feature_id: conflict.featureId,
                        })
                      }
                    >
                      {conflict.details.outreach_email as string}
                    </DetailCardLink>
                  ) : (
                    '-'
                  ),
                },
                {
                  label: 'Notes',
                  value: (conflict.details.notes as string) || conflict.agencyNotes || '-',
                },
              ]}
            />
          </PopoverContent>
        </Popover>
      );
    },
  },
];
